import React, { useEffect, useState } from 'react';
import styles from './StudentLinkPage.module.scss';
import logoImage from '../../assets/img/dark_logo.png';
import ImageHourGlass from './assets/hourglass.svg?react';
import { useParams } from 'react-router-dom';
import Spinner from '../../components/spinner/Spinner';
import { useIntercom } from 'react-use-intercom';
import { Content, Lesson, SllLesson, SllModule, useGetLessonInfoByLinkLazyQuery, useGetSllLessonInfoByLinkLazyQuery } from '@src/api/graphql-codegen';
import { sortByPosition } from '@src/utils/helpers';
import { StudentLessonProvider, useStudentLessonContent } from '@src/hooks/useStudentLessonContent';
import SelectedStudentContent from '@src/components/student-lesson-details/selected-content/SelectedStudentContent';
import { useQueryParams } from '@src/hooks';
import MaterialModal from './material-modal/MaterialModal';
import MaterialPDF from '@src/components/material-pdf/MaterialPDF';

type Props = {
  lesson: SllLesson | Lesson;
}

function StudentLinkComponent(props: Props) {
  const {lesson} = props

  const { content, sllModule, setSllModule, setSllLesson, setContent, setLesson, showMaterail, setShowMaterial } = useStudentLessonContent();
  const [studentContent, setStudentContent] = useState<any>();
  const [showMaterailPDF, setShowMaterialPDF] = useState(false);
  const [selectedMaterial, setSelectMaterial] = useState<string | undefined>();
  const [needPrint, setNeedPrint] = useState(false);
  const [firstOpen, setFirstOpen] = useState(false);

  useEffect(() => {
    if (lesson.__typename === 'SLLLesson') {
      const sortModules = [...(lesson.presentation ?? [])].sort(sortByPosition);
      setSllModule(sortModules?.at(0) as SllModule)
      setStudentContent(sortModules?.at(0))
      setSllLesson(lesson);
      const materialLesson = lesson?.materials?.find(material => (material?.name.toLowerCase().includes('student')))
      if (lesson?.materials && materialLesson) {
        setFirstOpen(true);
        setShowMaterial(true);
      }
    } else if (lesson.__typename === 'Lesson') {
      const sortModules = [...(lesson.modules ?? [])].sort(sortByPosition);
      setContent(sortModules?.at(0) as Content)
      setStudentContent(sortModules?.at(0))
      setLesson(lesson);
    }
  }, [lesson, setSllModule, setSllLesson, setContent, setLesson, setShowMaterial])

  useEffect(() => {
    if (sllModule) {
      setStudentContent(sllModule);
    }
  }, [sllModule])

  useEffect(() => {
    if (content) {
      setStudentContent(content);
    }
  }, [content])

  const handleShowMaterialPDF = (url: string) => {
    setNeedPrint(false);
    setShowMaterialPDF(true);
    setSelectMaterial(url);
  }

  const handlePrintMaterialPDF = (url: string) => {
    setNeedPrint(true);
    setShowMaterialPDF(true);
    setSelectMaterial(url);
  }

  const handleStartLesson = () => {
    setShowMaterialPDF(false);
    setNeedPrint(false);
    setSelectMaterial(undefined)
  }

  const handleCloseModal = () => {
    setShowMaterial(false);
    setFirstOpen(false);
  }

  return (
    <div>
      <div className={styles.teacherFSView}>
        <div className={styles.fsBackdrop}/>
        <div className={styles.lessonFSSubView}>
          {studentContent && <SelectedStudentContent lesson={lesson} content={studentContent} goBack={()=>{}} />}
        </div>
      </div>
      {showMaterail && <MaterialModal open={showMaterail} lesson={lesson as SllLesson} firstOpen={firstOpen} startLesson={handleCloseModal} handleShowMaterialPDF={handleShowMaterialPDF} handlePrintMaterialPDF={handlePrintMaterialPDF} />}
      {showMaterailPDF && selectedMaterial && <div className={styles.materialFSSubView}>
        <MaterialPDF
          needPrint={needPrint}
          data={selectedMaterial}
          isFullScreen={true}
          goBack={handleStartLesson}
        />
      </div>}
    </div>
  )
}

const StudentLessonModal = (props: Props) => {
  const { lesson } = props;
  return (
    <StudentLessonProvider >
      <StudentLinkComponent lesson={lesson} />
    </StudentLessonProvider>
  )
}

type Params = { id: string };

function StudentLinkPage() {
  const { shutdown } = useIntercom();
  const { id } = useParams<Params>();
  const params = useQueryParams();
  const sll = params.get('sll')

  const [expirationError, setExpirationError] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [showLesson, setShowLesson] = useState(false);
  const [lesson, setLesson] = useState<any>();

  useEffect(() => {
    shutdown();
  }, [shutdown])

  const [getSllLessonInfoByLink, { loading }] = useGetSllLessonInfoByLinkLazyQuery({
    onCompleted(res) {
      const d = res?.getSllLessonInfoByLink;
      if (d) {
        setLesson(d);
        setShowLesson(true);
      }
    },
    onError(err) {
      const isExpired = err?.message.includes('already expired') || err?.message === 'Exceeded the maximum number of exit tickets.';
      const isNotfound = err?.message === 'Invalid Lesson Link' || err?.message === 'Invalid SLL Lesson ID';

      if (isExpired) {
        setExpirationError(true);
      }
      if (isNotfound) {
        setInvalid(true)
      }
    }
  });

  const [getNormalLessonInfoByLink, { loading: normalLessonLoading }] = useGetLessonInfoByLinkLazyQuery({
    onCompleted(res) {
      const d = res?.getLessonInfoByLink;
      if (d) {
        setLesson(d);
        setShowLesson(true);
      }
    },
    onError(err) {
      const isExpired = err?.message.includes('already expired') || err?.message === 'Exceeded the maximum number of exit tickets.';
      const isNotfound = err?.message === 'Invalid Lesson Link' || err?.message === 'Invalid SLL Lesson ID';
      
      if (isExpired) {
        setExpirationError(true);
      }
      if (isNotfound) {
        setInvalid(true)
      }
    }
  });

  useEffect(() => {
    if (id) {
      if (sll === 'true') {
        getSllLessonInfoByLink({ variables: { input: { link: id } } })
      } else {
        getNormalLessonInfoByLink({ variables: { input: { link: id } } })
      }
    }
  }, [getSllLessonInfoByLink, getNormalLessonInfoByLink, id, sll]);

  useEffect(() => {
    if (showLesson) {
      document.getElementById("root")!.style.overflow = "hidden";
      document.getElementById("root")!.style.width = "auto";
      document.getElementById("root")!.style.height = "auto";
      document.body.style.overflowY = "hidden";
    }
  }, [showLesson])
  return (
    <div className={styles.wrapper}>
      {(loading || normalLessonLoading) ? <div className={styles.page}><Spinner /> </div> :
        <>
          {expirationError || invalid ? <div className={styles.page}>
            <div className={styles.mainForm}>
              <div className={styles.formErrorTitle}>Link Expired</div>
              <ImageHourGlass />
              <div className={styles.formSubtitle}>This link has Expired.</div>
              <div className={styles.formSubtitle}>
                {'Please ask your teacher to generate a new Link for this Lesson.'}
              </div>
              <div className={styles.logo}>
                <img src={logoImage} alt="Parachute Logo" />
              </div>
            </div>
          </div> : <>
            {showLesson && <StudentLessonModal lesson={lesson}/>}
          </>
          }
        </>
      }
    </div>
  );
}

export default StudentLinkPage;
